/**
 * Platform Organization Query Hook
 *
 * Fetches and updates a single organization for platform admin detail page.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlatformOrganization,
  updatePlatformOrganization,
  PlatformOrganizationDetail,
} from '../../api/platform';
import { SubscriptionTier } from '../../api/auth';
import { getErrorMessage } from '../../api/client';

interface UsePlatformOrganizationOptions {
  /** Enable/disable the query (default: true when id is provided) */
  enabled?: boolean;
}

export function usePlatformOrganization(id: string, options: UsePlatformOrganizationOptions = {}) {
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: ['platform', 'organizations', id],
    queryFn: () => getPlatformOrganization(id),
    enabled: enabled && !!id,
  });

  return {
    // Data
    organization: query.data ?? null,

    // States
    loading: query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,

    // Actions
    refetch: query.refetch,
  };
}

interface UseUpdatePlatformOrganizationOptions {
  onSuccess?: (data: PlatformOrganizationDetail) => void;
  onError?: (error: Error) => void;
}

export function useUpdatePlatformOrganization(options: UseUpdatePlatformOrganizationOptions = {}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, subscriptionTier }: { id: string; subscriptionTier: SubscriptionTier }) =>
      updatePlatformOrganization(id, { subscriptionTier }),
    onSuccess: (data) => {
      // Update the single organization cache
      queryClient.setQueryData(['platform', 'organizations', data.id], data);
      // Invalidate the organizations list to refresh tier in list view
      queryClient.invalidateQueries({ queryKey: ['platform', 'organizations'] });
      options.onSuccess?.(data);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    updateOrganization: mutation.mutate,
    updating: mutation.isPending,
    error: mutation.error ? getErrorMessage(mutation.error) : null,
  };
}
